// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { Component, Input, ViewChild } from "@angular/core";
import { IQuestionnaireScoringSelection, IScoringSelection } from "@comunicare/comunicare-interfaces";
import { ChartOptions } from "chart.js";
import * as Color from "color";
import * as moment from "moment";
import { BaseChartDirective, Label } from "ng2-charts";
import { Scoring } from "src/app/models/questionnaireScoring.model";

export interface IChartColor {
  backgroundColor;
  borderColor;
  pointBackgroundColor;
  pointBorderColor;
  pointHoverBackgroundColor;
  pointHoverBorderColor;
}

/**
 * Container for observation data in Chart
 */
export class DataChart {
  public data: number[];
  public label: string;
  public type: string;
  public pointRadius = 4;
  public pointHoverRadius = 6;
  public yAxisID: string;
  // colors
  public fill: boolean;

  [key: string]: unknown; // allow any properties

  constructor() {
    this.label = "";
    this.data = new Array<number>();
    this.type = "bar";
    this.fill = false;
  }
}

/**
 * Observation Component
 */
@Component({
  selector: "app-questionnaire-scoring-graph",
  templateUrl: "questionnaireScoringGraph.html",
})
export class QuestionnaireScoringGraphComponent {
  @ViewChild("baseChart") chart: BaseChartDirective;

  @Input() scoringSelection: IQuestionnaireScoringSelection[] = [];
  prevScoringSelection: IQuestionnaireScoringSelection[];
  @Input() MAX_CHART_LAST_ITEMS = 999;
  @Input() chartH: number;
  @Input() chartW: number;
  @Input() set scoringsData(s: Scoring[]) {
    if (s?.length) {
      this.scorings = s;
      this.computeChartData();
    }
  }

  public scorings: Scoring[];
  public colors: string[] = [];
  public chartColors: Array<IChartColor> = [];

  chartLabels: Array<Date> = [];
  chartData: DataChart[] = [];
  // observations charts parameters
  chartOptions = {
    animation: {
      duration: 0, // general animation time
    },
    elements: {
      line: { tension: 0 }, // curve of the line
    },
    hover: {
      animationDuration: 0, // duration of animations when hovering an item
    },
    responsiveAnimationDuration: 0, // animation duration after a resize
    responsive: true,
    scales: {
      yAxes: [],
      xAxes: [
        {
          type: "time",
          distribution: "linear",
          time: {
            source: "labels",
            isoWeekday: true,
            minUnit: "day",
            maxUnit: "month",
            displayFormats: {
              day: "DD/MM/YY",
              week: "DD/MM/YY",
              month: "DD/MM/YY",
            },
          },
          gridLines: {
            offsetGridLines: true,
            display: false,
            drawBorder: true,
          },
        },
      ],
    },
    legend: {
      display: true,
      onClick: null,
    },
  };
  chartMeta: Array<Record<string, unknown>> = [];

  /**
   * Constructor
   */
  constructor() {
    this.computeChartColors();
  }

  public toRGBA(color: Color<string>, alpha: number): string {
    const r = Math.round(color.red());
    const g = Math.round(color.green());
    const b = Math.round(color.blue());
    return `rgba(${r},${g},${b},${alpha})`;
  }

  /**
   * Compute colors
   */
  private computeChartColors() {
    this.chartColors = new Array<IChartColor>();
    this.colors = this.computeColors();
    for (const c of this.colors) {
      const color = Color(c);
      this.chartColors.push({
        backgroundColor: this.toRGBA(color, 0.8),
        borderColor: this.toRGBA(color, 1),
        pointBackgroundColor: this.toRGBA(color, 1),
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: this.toRGBA(color, 0.8),
      });
    }
  }

  /**
   * Build Colors for chart
   */
  private computeColors(): string[] {
    const colors = [
      "#005FB3", // blue
      "#FA9700", // orange
      "#1F831F", // green
      "#831F81", // purple
      "#83201F", // red
      "#CC2BB1", // pink
      "#727272", // gray
    ];
    // if 7 colors are not enough, add random colors
    for (let i = 0; i < 20; i++) {
      colors.push(this.getRandomColor());
    }
    return colors;
  }

  /**
   * Generate random colors
   */
  private getRandomColor(): string {
    const letters = "0123456789ABCDEF".split("");
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  /**
   * Build Chart Data and layout
   */
  public computeChartData(): void {
    const chartLabels = new Array<Date>();
    const tmpChartData = new Array<DataChart>();
    // sort score in from more recent to older
    this.scorings = this.scorings.sort((a, b) => (moment(a.date).isAfter(moment(b.date)) ? -1 : 1));

    for (const score of this.scorings) {
      chartLabels.push(moment(score.questionnaireResponse.authored).toDate());
    }
    // for better layout, add empty date-value at begin and end of chart
    if (chartLabels?.length) {
      const firstMoment = moment(chartLabels[0]);
      const lastMoment = moment(chartLabels[chartLabels.length - 1]);
      chartLabels.unshift(firstMoment.add(1, "day").toDate());
      chartLabels.push(lastMoment.add(-1, "day").toDate());
    }

    let i = 0;
    const scoringSelected: IScoringSelection[] = [];

    for (const qs of this.scoringSelection) {
      for (const s of qs.scoringIdentifier) {
        if (s.selected) {
          scoringSelected.push(s);
          const dataChart = new DataChart();
          dataChart.type = "bar";
          dataChart.axisColor = this.colors[i];
          dataChart.fill = true;
          dataChart.borderWidth = 3;
          dataChart.maxBarThickness = 3;
          dataChart.barThickness = 15;
          // set label
          dataChart.label = s.name;
          // push data
          dataChart.data.push(null);
          for (const score of this.scorings) {
            const scoreValue = this.getScoring(score, s.id);
            dataChart.data.push(scoreValue);
            // do not push to many data (max 999)
            if (this.MAX_CHART_LAST_ITEMS !== 0 && dataChart.data.length >= this.MAX_CHART_LAST_ITEMS) {
              break;
            }
          }
          dataChart.data.push(null);
          tmpChartData.push(dataChart);
          ++i;
        }
      }
    }

    this.chartOptions.scales.yAxes = scoringSelected.map((scorSel, index) => {
      // compute min/max value for Y axe
      const minmax = this.computeMinMax(scorSel);
      return {
        id: scorSel.id,
        ticks: {
          fontColor: this.colors[index],
          suggestedMin: minmax.min,
          suggestedMax: minmax.max,
        },
      };
    });

    this.chartData = [];
    this.chartLabels = [];
    this.chartData = tmpChartData;
    this.chartLabels = chartLabels;
    this.reloadChart();
  }

  /**
   * return score value for a reference or null if not found
   */
  private getScoring(score: Scoring, reference: string) {
    for (const s of score.scoring) {
      if (s.identifier?.value === reference) {
        return s.scoringValue;
      }
    }
    return null;
  }

  private computeMinMax(scoreSelected: IScoringSelection): { min: number; max: number } {
    let min = Number.MAX_VALUE;
    let max = Number.MIN_VALUE;
    for (const s of this.scorings) {
      const value = this.getScoring(s, scoreSelected.id);
      if (value) {
        if (value < min) {
          min = value;
        }
        if (value > max) {
          max = value;
        }
      }
    }
    let variation = Math.round(max / 10);
    if (variation === 0) {
      variation = 1;
    }
    min = Math.round(min - variation);
    max = Math.round(max + variation);
    // "min-1" to be able to see minimum value (or it will be on the X axis)
    const minmax = { min: min > 0 ? min - 1 : min, max };
    return minmax;
  }

  /**
   * Reload chart: Fix for the charts not updating correctly
   */
  private reloadChart() {
    if (this.chart) {
      this.chart.chart?.destroy();
      this.chart.ngOnDestroy();
      this.chart.chart = null;
      if (this.chart.chart) {
        this.chart.datasets = this.chartData;
      }
      this.chart.labels = this.chartLabels as unknown[] as Label[];
      this.chart.options = this.chartOptions as ChartOptions;
      if (this.chart.datasets?.length) {
        this.chart.ngOnInit();
      }
    }
  }
}
